import type { ChangeEvent } from 'react';

import {
  AddImageInvertedIcon,
  IconButton,
  PencilIcon,
  Spinner,
  TrashIcon,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../../../../../components/avatar';

import styles from './speaker-image-uploader.module.css';

const ACCEPTED_IMAGE_FORMATS = '.jpg,.jpeg,.png,.svg,.gif';
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB

type Props = {
  testIdPrefix: string;
  name: string;
  imageUrl?: string | null;
  uploading?: boolean;
  disabled?: boolean;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
};

export function SpeakerImageUploader({
  testIdPrefix,
  name,
  imageUrl,
  uploading,
  disabled,
  onFileSelected,
  onRemove,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  const openPicker = () => inputRef.current?.click();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      setError(t('manager.webcastSetup.speakers.image.restrictions'));
      return;
    }

    setError('');
    onFileSelected(file);
  };

  const interactive = !disabled && !uploading;

  return (
    <div className={styles.wrapper}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_FORMATS}
        className={styles.input}
        data-testid={`${testIdPrefix}-input`}
        disabled={disabled}
        onChange={handleChange}
      />

      <Avatar
        className={styles.avatar}
        // Empty state: the whole avatar is the "add image" button. Once there is an image the avatar
        // is a plain element and the overlay's change/remove buttons take over — this avoids nesting
        // buttons inside a button.
        onClick={interactive && !imageUrl ? openPicker : undefined}
        aria-label={t('manager.webcastSetup.speakers.image.label')}
        data-testid={`${testIdPrefix}-trigger`}
      >
        {imageUrl ? (
          <img
            className={classNames(styles.image, uploading && styles.imageProcessing)}
            src={imageUrl}
            alt={t('manager.webcastSetup.speakers.image.alt', { name })}
          />
        ) : (
          <AddImageInvertedIcon />
        )}

        {uploading && (
          <span className={styles.progress} data-testid={`${testIdPrefix}-spinner`}>
            <Spinner size={24} />
          </span>
        )}

        {interactive && imageUrl && (
          // Dark-theme island: the controls sit on a dark scrim, so flip the library colour tokens
          // (IconButton renders its icon in `--mi-label-main`) to their light values.
          <span className={styles.overlay} data-theme="dark">
            <IconButton
              rounded
              type="button"
              data-testid={`${testIdPrefix}-change`}
              aria-label={t('manager.webcastSetup.speakers.image.change')}
              onClick={openPicker}
            >
              <PencilIcon />
            </IconButton>

            <IconButton
              rounded
              type="button"
              data-testid={`${testIdPrefix}-remove`}
              aria-label={t('manager.webcastSetup.speakers.image.remove')}
              onClick={onRemove}
            >
              <TrashIcon />
            </IconButton>
          </span>
        )}
      </Avatar>

      {error && (
        <span className={styles.error} data-testid={`${testIdPrefix}-error`}>
          {error}
        </span>
      )}
    </div>
  );
}
